import API_CONFIG from './config';

export class HttpError extends Error {
  constructor(message, status, data = null, fieldErrors = null) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
    this.fieldErrors = fieldErrors;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network error. Please check your internet connection.') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(message = 'Request timed out. Please try again.') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class ValidationError extends Error {
  constructor(message, fieldErrors = {}) {
    super(message);
    this.name = 'ValidationError';
    this.fieldErrors = fieldErrors;
  }
}

let isRefreshing = false;
let refreshSubscribers = [];

function onTokenRefreshed() {
  refreshSubscribers.forEach((callback) => callback());
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

function getAccessToken() {
  return localStorage.getItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
}

function getRefreshToken() {
  return localStorage.getItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
}

function setTokens(accessToken, refreshToken) {
  localStorage.setItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
}

function clearAuth() {
  localStorage.removeItem(API_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(API_CONFIG.STORAGE_KEYS.USER);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetry(error, attempt) {
  if (attempt >= API_CONFIG.RETRY.MAX_RETRIES) return false;
  if (error instanceof NetworkError || error instanceof TimeoutError) return true;
  if (error instanceof HttpError) {
    return API_CONFIG.RETRY.RETRY_STATUS_CODES.includes(error.status);
  }
  return false;
}

async function fetchWithTimeout(url, options) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new TimeoutError();
    }
    throw new NetworkError();
  }
}

async function tryRefreshToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const json = await response.json();
      const data = json.data;
      if (data && data.accessToken) {
        setTokens(data.accessToken, data.refreshToken);
        return true;
      }
    }
  } catch (err) {
    console.error('Token refresh failed:', err);
  }

  return false;
}

async function request(url, options = {}, attempt = 0) {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Don't set Content-Type for FormData (let browser set multipart boundary)
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const fullURL = url.startsWith('http') ? url : `${API_CONFIG.BASE_URL}${url}`;

    const response = await fetchWithTimeout(fullURL, {
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type');
    let responseData = null;

    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      // Handle 401/403 - try token refresh
      if ((response.status === 401 || response.status === 403) && attempt === 0) {
        if (!isRefreshing) {
          isRefreshing = true;
          const refreshed = await tryRefreshToken();
          isRefreshing = false;

          if (refreshed) {
            onTokenRefreshed();
            return request(url, options, attempt + 1);
          } else {
            clearAuth();
            window.dispatchEvent(new CustomEvent('auth:logout'));
            throw new HttpError('Session expired. Please login again.', 401, responseData);
          }
        } else {
          // Another request is already refreshing, wait for it
          return new Promise((resolve, reject) => {
            addRefreshSubscriber(() => {
              request(url, options, attempt + 1).then(resolve).catch(reject);
            });
          });
        }
      }

      // Handle rate limiting (429) — do NOT retry
      if (response.status === 429) {
        throw new HttpError(
          'Too many requests. Please wait a moment and try again.',
          429,
          responseData
        );
      }

      // Handle validation errors (400 with fieldErrors)
      if (response.status === 400 && responseData && responseData.fieldErrors) {
        throw new ValidationError(
          responseData.message || 'Validation failed',
          responseData.fieldErrors
        );
      }

      throw new HttpError(
        responseData?.message || `HTTP Error: ${response.status}`,
        response.status,
        responseData,
        responseData?.fieldErrors
      );
    }

    // Unwrap ApiResponse - return the inner data
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      return {
        data: responseData.data,
        message: responseData.message,
        success: responseData.success,
      };
    }

    return { data: responseData, success: true };
  } catch (error) {
    if (shouldRetry(error, attempt)) {
      const delay = API_CONFIG.RETRY.BASE_DELAY * Math.pow(2, attempt);
      await sleep(delay);
      return request(url, options, attempt + 1);
    }
    throw error;
  }
}

const httpClient = {
  get: (url, params = {}) => {
    const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const queryString = new URLSearchParams(filteredParams).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return request(fullUrl, { method: 'GET' });
  },

  post: (url, data = {}) => {
    return request(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: (url, data = {}) => {
    return request(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: (url) => {
    return request(url, { method: 'DELETE' });
  },

  postMultipart: (url, formData) => {
    return request(url, {
      method: 'POST',
      body: formData,
    });
  },
};

export default httpClient;
